import styled from '@emotion/styled';
import { forwardRef } from 'react';

import { themeStyles, themeVars } from '../../../ui-components/theme/theme';
import type { AddPrefixToObjectKeys } from '../../../utilities/types/AddPrefixToObjectKeys';
import { FrontModalCloseButton } from './FrontModalCloseButton';

export const frontModalHeaderPositions = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
} as const;
export type FrontModalTitleTextPosition = keyof typeof frontModalHeaderPositions;
export interface FrontModalHeaderProps {
  textPosition?: FrontModalTitleTextPosition;
  title?: string;
  subTitle?: string;
  onClose?: () => void;
  hideCloseButton?: boolean;
}
export type FrontModalHeaderOutsideProps = Pick<
  FrontModalHeaderProps,
  'title' | 'subTitle' | 'hideCloseButton'
> & {
  headerTextPosition?: FrontModalTitleTextPosition;
};
export const FrontModalHeaderInside = forwardRef<HTMLDivElement, FrontModalHeaderProps>(
  ({ textPosition = 'left', title, subTitle, onClose, hideCloseButton = false }, ref) => {
    const leftCloseButton = !hideCloseButton && ['right', 'center'].includes(textPosition);
    const rightCloseButton = !hideCloseButton && ['left', 'center'].includes(textPosition);

    return (
      <Wrapper ref={ref}>
        {leftCloseButton && <FrontModalCloseButton onClick={onClose} />}
        <TextWrapper data-textPosition={textPosition}>
          <Title>{title}</Title>
          {subTitle && <Subtitle>{subTitle}</Subtitle>}
        </TextWrapper>
        {rightCloseButton && <FrontModalCloseButton onClick={onClose} />}
      </Wrapper>
    );
  },
);

const Wrapper = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${themeVars.spacings.sp04};
  padding: ${themeVars.spacings.sp06};

  background-color: ${themeVars.colors.white};
  border-radius: 16px 16px 0 0;
`;

type TextWrapperProps = AddPrefixToObjectKeys<Pick<FrontModalHeaderProps, 'textPosition'>, 'data-'>;
const TextWrapper = styled('div')<TextWrapperProps>`
  display: flex;
  flex-direction: column;
  gap: ${themeVars.spacings.sp03};
  flex: 1;
  min-width: 0;
  &[data-textPosition='left'] > * {
    text-align: start;
  }
  &[data-textPosition='right'] > * {
    text-align: start;
  }
  &[data-textPosition='center'] > * {
    text-align: center;
  }
`;
const Title = styled('div')`
  ${themeStyles.text.telkaEx.head.sm}
  color: ${themeVars.colors.coldGrey900};
`;

const Subtitle = styled('div')`
  ${themeStyles.text.telka.body.lg}
  color: ${themeVars.colors.coldGrey600};
`;

export const FrontModalHeader = Object.assign(FrontModalHeaderInside, {
  Wrapper,
  TextWrapper,
  Title,
  Subtitle,
});
